import { ComponentProps, useEffect, useState } from 'react';
import { DayPicker, TZDate } from 'react-day-picker';
import 'react-day-picker/style.css';
import { DateRangePickerLayout } from '../../shared/DateRangePickerLayout';
import { getDayPickerLocale } from '../../shared/day-picker-locale';
import { useSharedDayPickerStyles } from '../../shared/useSharedDayPickerStyles';

/** Default: disable days after today (no future dates). */
const DEFAULT_DISABLED: ComponentProps<typeof DayPicker>['disabled'] = {
  after: new Date(),
};

function calendarYearInPickerZone(date: Date, timeZone: string | undefined): number {
  return timeZone != null ? new TZDate(date, timeZone).getFullYear() : date.getFullYear();
}

export interface SingleDatePickerProps {
  open: boolean;
  onClose: () => void;
  /** Called with the selected date (start of day). Caller builds range if needed. */
  onChange: (date: Date) => void;
  /** Initial date for the picker. */
  initialDate?: Date | null;
  /** Which days are disabled in the calendar. Default: no future dates (after today). */
  disabled?: ComponentProps<typeof DayPicker>['disabled'];
  /** Earliest month included in the caption year dropdown (month stays a label; use with {@link endMonth}). */
  startMonth?: Date;
  /** Latest month included in the caption year dropdown. When omitted, DayPicker uses its default span. */
  endMonth?: Date;
  /** IANA time zone (e.g. "America/New_York") or UTC offset. When set, calendar uses TZDate so dates render in that zone. */
  tz?: string;
  /** App locale string (e.g. from useIntl().locale). Converted to DayPicker locale inside this component. */
  locale?: string;
}

/**
 * Single-date picker: click a day to apply. Calls onChange with the selected date (start of day).
 * When tz is provided, uses TZDate so the calendar and selection are in that time zone.
 * If {@link startMonth} and {@link endMonth} are set and share the same calendar year in that zone,
 * the caption is a plain label; otherwise the year is a dropdown.
 * Renders inside Menu/Popover with no title or action buttons.
 */
export function SingleDatePicker({
  open,
  onClose,
  onChange,
  initialDate,
  disabled,
  startMonth,
  endMonth,
  tz,
  locale,
}: SingleDatePickerProps) {
  const effectiveDisabled = disabled ?? DEFAULT_DISABLED;
  const dayPickerLocale = locale != null ? getDayPickerLocale(locale) : undefined;
  const dayPickerStyles = useSharedDayPickerStyles();

  const [selected, setSelected] = useState<Date | undefined>(() => {
    if (!initialDate) return undefined;
    return tz ? new TZDate(initialDate, tz) : new Date(initialDate);
  });

  useEffect(() => {
    if (open) {
      setSelected(
        initialDate ? (tz ? new TZDate(initialDate, tz) : new Date(initialDate)) : undefined,
      );
    }
  }, [open, initialDate, tz]);

  const applyDate = (date: Date | undefined) => {
    if (!date) {
      return;
    }

    onChange(date);
    onClose();
  };

  const defaultMonth = selected ?? (tz ? TZDate.tz(tz) : new Date());

  const pickerStartMonth =
    startMonth != null ? (tz ? new TZDate(startMonth, tz) : new Date(startMonth)) : undefined;
  const pickerEndMonth =
    endMonth != null ? (tz ? new TZDate(endMonth, tz) : new Date(endMonth)) : undefined;

  const onlyOneSelectableYear =
    startMonth != null &&
    endMonth != null &&
    calendarYearInPickerZone(startMonth, tz) === calendarYearInPickerZone(endMonth, tz);

  const captionLayout = onlyOneSelectableYear ? 'label' : 'dropdown-years';

  return (
    <DateRangePickerLayout
      onCancel={onClose}
      onApply={() => applyDate(selected)}
      applyDisabled={!selected}
      hideActions
    >
      <DayPicker
        mode="single"
        captionLayout={captionLayout}
        selected={selected}
        onSelect={(date) => {
          setSelected(date ?? undefined);

          if (date) {
            applyDate(date);
          }
        }}
        defaultMonth={defaultMonth}
        startMonth={pickerStartMonth}
        endMonth={pickerEndMonth}
        disabled={effectiveDisabled}
        locale={dayPickerLocale}
        styles={dayPickerStyles}
        timeZone={tz}
      />
    </DateRangePickerLayout>
  );
}
